/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classnames from 'classnames';
import { ChoiceGroup } from '@bingads-webui-react/fabric-control';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { ImageExperienceImage, constants as C } from '@bingads-webui-campaign-react/image-experience-model';
import { Button } from '@fluentui/react-components';
import { Delete20Regular } from '@fluentui/react-icons';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { EditableImageButton } from './editable-image-button';
import { SparkleMenuButton } from './sparkle-menu-button';

const logSignal = (props, message) => {
  if (props.logger && props.logger.signal) {
    props.logger.signal(`${message}: isLogo:${props.isLogo} isPortrait:${props.isPortrait}`, 'ResponsiveImageListView');
  }
};

export const handleMouseEnter = (props) => {
  logSignal(props, 'mouse enter image-list-item');
  setClarityTag({ HoverResponsiveImageListView: 'Hover' });
  if (props.showCopilotOption) {
    logSignal(props, 'mouse enter image-list-item: show copilot option');
    setClarityTag({ HoverResponsiveImageListView: 'HoverCopilotMenu' });
  }
};

export const handleMouseLeave = (props) => {
  logSignal(props, 'mouse leave image-list-item');
  if (props.showCopilotOption) {
    logSignal(props, 'mouse leave image-list-item: show copilot option');
  }
};

export class ResponsiveImageListView extends React.PureComponent {
  constructor(props) {
    super(props);
    // Only for test purpose
    if (this.props.testRef) {
      this.props.testRef.current = {
        that: this,
      };
    }
  }

  componentDidMount() {
    logSignal(this.props, 'mount');
    if (this.props.showCopilotOption) {
      logSignal(this.props, 'mount: show copilot option');
    }
    if (this.props.useNewEditorIcon) {
      logSignal(this.props, 'mount: use new editor icon');
    }
  }

  onSelect = (_event, option) => {
    if (!option.isAddButton) {
      this.props.onSelectImage(option.key, option.isLogo);
    }
  };

  onRemove = (event) => {
    logSignal(this.props, 'remove');
    if (this.props.showCopilotOption) {
      logSignal(this.props, 'remove: show copilot option');
    }
    if (this.props.useNewEditorIcon) {
      logSignal(this.props, 'remove: use new editor icon');
    }
    this.props.onRemoveImage(event.currentTarget.dataset.imageid, this.props.isLogo, this.props.isPortrait);
  };

  onEdit = (event) => {
    logSignal(this.props, 'edit');
    if (this.props.showCopilotOption) {
      logSignal(this.props, 'edit: show copilot option');
    }
    if (this.props.useNewEditorIcon) {
      logSignal(this.props, 'edit: use new editor icon');
    }
    this.props.onEditImage(event.currentTarget.dataset.imageid, this.props.isLogo, this.props.isPortrait);
  };

  onChangeBackgroundScene = (event) => {
    logSignal(this.props, 'change background');
    this.props.onChangeBackgroundScene(event.currentTarget.dataset.imageid, this.props.isLogo, this.props.isPortrait);
  };

  getKeyForImageItem(placements) {
    const [firstPlacement] = placements;
    const baseImageId = firstPlacement.BaseId;
    return baseImageId;
  }

  getChoiceGroupOption() {
    return _.map(this.props.imageList, (placements, index) => {
      const imageItem = _.first(placements);
      const baseImageId = this.getKeyForImageItem(placements);

      if (imageItem) {
        const contentWidth = 112; // choice box width
        const contentHeight = 99;
        // ad creator restyles buttons, adjust height accordingly
        let desiredImageWidth = contentWidth;
        let desiredImageHeight = contentHeight;

        const styles = {};

        let imageSrc = imageItem.Src;
        // distinguish the thumbnail of logo
        const isLandscapeLogoMedia = imageItem.isLogo && imageItem.id.indexOf('LandscapeLogoMedia') > -1;

        // if there is image crop, re-locate the image based on the crop box
        const ratio = contentHeight / imageItem.Height > contentWidth / imageItem.Width ?
          contentHeight / imageItem.Height : contentWidth / imageItem.Width;

        desiredImageHeight = imageItem.Height * ratio;
        desiredImageWidth = imageItem.Width * ratio;

        let midPointOffsetX = desiredImageWidth > contentWidth ?
          Math.ceil(-(desiredImageWidth - contentWidth) / 2) : 0;
        let midPointOffsetY = desiredImageHeight > contentHeight ?
          Math.ceil(-(desiredImageHeight - contentHeight) / 2) : 0;

        _.extend(styles, {
          field: {
            alignItems: 'center',
            justifyContent: 'start',
            overflow: 'hidden',
          },
          selectedImageWrapper: {
            marginLeft: midPointOffsetX,
            marginTop: midPointOffsetY,
          },
          imageWrapper: {
            marginLeft: midPointOffsetX,
            marginTop: midPointOffsetY,
          },
        });

        /* istanbul ignore if */
        if (isLandscapeLogoMedia) {
          let landscapeLogoRatio = C.getMmaAspectRatios('Logo');
          // get the ratio :4
          landscapeLogoRatio = landscapeLogoRatio.Image4x1.aspectRatio;
          const landscapeLogoMarginTop = (contentHeight - (contentHeight / landscapeLogoRatio)) / 2;
          const landscapeLogoShowHeight = contentHeight / landscapeLogoRatio;
          const landscapeLogoTop = ((desiredImageHeight - contentHeight) / 2) + landscapeLogoMarginTop;

          _.extend(styles, {
            innerField: {
              height: `${landscapeLogoShowHeight}px !important`,
              overflow: 'hidden',
              marginTop: landscapeLogoMarginTop,
            },
            selectedImageWrapper: {
              position: 'absolute',
              top: -landscapeLogoTop,
            },
            imageWrapper: {
              position: 'absolute',
              top: -landscapeLogoTop,
            },
          });
        }

        const imageCropSettings = imageItem.BaseImage && imageItem.BaseImage.CropSettings;
        imageSrc = imageItem.BaseImage.FinalSrc;

        if (imageCropSettings
            && imageCropSettings.SourceWidth > 0
            && imageCropSettings.SourceHeight > 0) {
          let desiredCropWidth = contentWidth;
          let desiredCropHeight = contentHeight;

          const cropImageWidth = imageCropSettings.SourceWidth;
          const cropImageHeight = imageCropSettings.SourceHeight;

          const cropRatio = contentHeight / cropImageHeight > contentWidth / cropImageWidth ?
            contentHeight / cropImageHeight : contentWidth / cropImageWidth;

          desiredCropHeight = cropImageHeight * cropRatio;
          desiredCropWidth = cropImageWidth * cropRatio;

          const xscale = desiredCropWidth / imageCropSettings.SourceWidth;
          const yscale = desiredCropHeight / imageCropSettings.SourceHeight;

          // use original image width/height and use css to re-position to show cropped area
          desiredImageWidth = imageItem.Width * xscale;
          desiredImageHeight = imageItem.Height * yscale;

          midPointOffsetX = desiredCropWidth > contentWidth ?
            Math.ceil(-(desiredCropWidth - contentWidth) / 2) : 0;
          midPointOffsetY = desiredCropHeight > contentHeight ?
            Math.ceil(-(desiredCropHeight - contentHeight) / 2) : 0;

          _.extend(styles, {
            selectedImageWrapper: {
              marginLeft: (-imageCropSettings.SourceX * xscale) + midPointOffsetX,
              marginTop: (-imageCropSettings.SourceY * yscale) + midPointOffsetY,
            },
            imageWrapper: {
              marginLeft: (-imageCropSettings.SourceX * xscale) + midPointOffsetX,
              marginTop: (-imageCropSettings.SourceY * yscale) + midPointOffsetY,
            },
          });
        }

        const {
          i18n,
          isLogo,
          showCopilotOption,
          enableEdit,
          useNewEditorIcon,
        } = this.props;

        return {
          key: baseImageId,
          imageSrc,
          selectedImageSrc: imageSrc,
          imageSize: {
            /* istanbul ignore next */
            width: desiredImageWidth, height: desiredImageHeight,
          },
          text: '',
          isLogo,
          styles,
          ariaLabel: `${i18n.getString(_TL_('Image'))} ${index + 1}`,
          onRenderLabel: () => null,
          onRenderField: (props, render) => {
            const [open, setOpen] = useState(false);
            const onMeuOpenChange = useCallback((e, data) => {
              logSignal(this.props, `open copilot menu: ${data.open}`);
              if (data.open) {
                setClarityTag({ HoverResponsiveImageListView: 'OpenCopilotMenu' });
              }
              setOpen(data.open);
            }, []);


            return (
              <div
                className="image-list-item"
                onMouseEnter={() => handleMouseEnter(this.props)}
                onMouseLeave={() => handleMouseLeave(this.props)}
                key={imageItem.Type}
                style={{ overflow: 'hidden' }}
                data-imageid={baseImageId}
              >
                <div className={`image-list-item-image ${showCopilotOption ? 'with-copilot-option' : ''} ${useNewEditorIcon ? 'with-new-editor-icon' : ''}`}>
                  { render(props) }
                  <div className={`image-list-item-action-bar ${open ? 'open' : ''}`}>
                    {showCopilotOption &&
                      <SparkleMenuButton
                        baseImageId={baseImageId}
                        onChangeBackgroundScene={this.onChangeBackgroundScene}
                        imageItem={imageItem}
                        i18n={i18n}
                        onMeuOpenChange={onMeuOpenChange}
                      />
                    }
                    {enableEdit &&
                    <EditableImageButton
                      baseImageId={baseImageId}
                      onEdit={this.onEdit}
                      imageItem={imageItem}
                      i18n={i18n}
                      useNewEditorIcon={useNewEditorIcon}
                    />}
                    {showCopilotOption && <div className="action-divider" />}
                    {useNewEditorIcon ? (<Button
                      aria-label={i18n.getString(_TL_('Remove this image'))}
                      className="image-delete"
                      onClick={this.onRemove}
                      data-imageid={baseImageId}
                      icon={<Delete20Regular />}
                    />) : (<button
                      aria-label={i18n.getString(_TL_('Remove this image'))}
                      className="image-delete glyphicon glyphicon-remove"
                      onClick={this.onRemove}
                      data-imageid={baseImageId}
                    />)}
                    {
                      imageItem.isRecommendedImage && <span
                        aria-label={i18n.getString(_TL_('AI generated asset'))}
                        className="iconba iconba-Lightbulb"
                      />
                    }
                  </div>
                </div>
              </div>
            );
          },
        };
      }

      return null;
    });
  }

  addButtonChoiceOption = ({
    key: 'choice-group-add-img',
    isAddButton: true,
    text: '',
    styles: {
      field: {
        alignItems: 'center',
        justifyContent: 'start',
        overflow: 'hidden',
        '&:before, &:after': {
          visibility: 'hidden',
        },
      },
    },
    ariaLabel: 'choice-group-add-img',
    onRenderLabel: () => null,
    onRenderField: (props, render) => (
      <button
        className="image-list-item image-list-add-placeholder"
        style={{ overflow: 'hidden' }}
        onClick={this.props.onAddImage}
        aria-label={this.props.i18n.getString(_TL_('Add an image'))}
      >
        <div className="image-list-item-image">
          { render(props) }
          <div
            className="glyphicon glyphicon-plus image-add"
          />
        </div>
      </button>
    ),
  });

  render() {
    const { portalContainer, blocking } = this.props;

    const choiceGroupOptions = this.getChoiceGroupOption();
    if (this.props.isRenderAddInImageList) {
      choiceGroupOptions.push(this.addButtonChoiceOption);
    }

    const renderContent = (
      <UiBlocker
        blocking={blocking}
        loadingText={this.props.i18n.getString(_TL_('Loading...'))}
      >
        <div className="image-list-view-content">
          <div className="image-list">
            <div className="image-list-content">
              <ChoiceGroup
                className={classnames('image-list-choice-group', 'image-list-choice-group-ad-creator')}
                label={this.props.i18n.getString(_TL_('Images'))}
                defaultSelectedKey={_.isEmpty(this.props.imageList) ? null : this.getKeyForImageItem(this.props.imageList[0])}
                selectedKey={this.props.currentSelectedImageId}
                options={choiceGroupOptions}
                onChange={this.onSelect}
                style={{ height: 'fit-content' }}
              />
              <button
                className="remove-all-button"
                onClick={this.props.onRemoveAllImages}
                aria-label={this.props.i18n.getString(_TL_('Remove all images'))}
              />
            </div>
          </div>
          {this.props.error ? (
            <div className="error" role="alert">
              {this.props.error}
            </div>
          ) : null}
        </div>
      </UiBlocker>
    );

    return portalContainer ? ReactDOM.createPortal(renderContent, portalContainer) : renderContent;
  }
}

ResponsiveImageListView.propTypes = {
  imageList: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.instanceOf(ImageExperienceImage)))
    .isRequired,
  onRemoveAllImages: PropTypes.func.isRequired,
  onRemoveImage: PropTypes.func.isRequired,
  onEditImage: PropTypes.func,
  onChangeBackgroundScene: PropTypes.func,
  onSelectImage: PropTypes.func.isRequired,
  isRenderAddInImageList: PropTypes.bool,
  onAddImage: PropTypes.func,
  currentSelectedImageId: PropTypes.any,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  isLogo: PropTypes.bool,
  portalContainer: PropTypes.instanceOf(Element),
  showCopilotOption: PropTypes.bool,
  enableEdit: PropTypes.bool,
  blocking: PropTypes.bool,
  error: PropTypes.string,
  isPortrait: PropTypes.bool,
  logger: PropTypes.shape({
    signal: PropTypes.func.isRequired,
  }),
  useNewEditorIcon: PropTypes.bool,
};

ResponsiveImageListView.defaultProps = {
  isRenderAddInImageList: false,
  onAddImage: null,
  currentSelectedImageId: null,
  onEditImage: _.noop,
  onChangeBackgroundScene: _.noop,
  isLogo: false,
  portalContainer: null,
  showCopilotOption: false,
  enableEdit: true,
  blocking: false,
  error: undefined,
  isPortrait: false,
  logger: {
    signal: _.noop,
    trace: _.noop,
  },
  useNewEditorIcon: false,
};
